// 📁 src/pages/Pentacles.js
import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/seo";
import cardData from "../components/card-data";
import { cardPath } from "../utils/cardRoutes";
import { COMMON_COPY_ES, isSpanishLocale, localizedPath, translateCardName, translateKeywordPhrase, translateSuitName } from "../utils/i18n";

/* =========================================================
   THEME TOKENS (match MajorArcana.js / Wands.js / Cups.js / Swords.js)
========================================================= */
const WRAP = "px-[1vw]";
const MAXW = "max-w-10xl mx-auto";
const FONT = "font-[Cormorant_Garamond]";
const TEXT = "text-white/90";
const BORDER_SOFT = "border-[#005C83]/12";
const PANEL_BASE =
  "relative overflow-hidden rounded-md " +
  BORDER_SOFT +
  " shadow-[0_2px_4px_rgba(0,0,0,0.35)]";

/* Canvas background */
const CANVAS_BG = `
  radial-gradient(1200px 900px at 50% -12%, rgba(3,121,155,0.32), rgba(3,121,155,0) 40%),
  linear-gradient(180deg, #014C75 0%, #005C83 22%, #016280ff 38%, #0C4B84 72%, #014C75 90%, #821E3C 100%)
`;

/* Silk panel */
const SILK_TURQ =
  "bg-[linear-gradient(135deg,rgba(12,63,107,0.4),rgba(20,33,61,0.88))] " +
  "before:absolute before:inset-0 before:bg-[radial-gradient(80%_60%_at_80%_0%,rgba(59,198,214,0.25),transparent_100%)] before:content-['']";

/* =========================================================
   SEO (About.js-style via <Seo />)
========================================================= */
const siteUrl = "https://tarotfate.com";
const pageUrl = `${siteUrl}/tarot/pentacles`;
const ogImage = `${siteUrl}/icons/og-cover.jpg`;   // social share image
const logoWebp = `${siteUrl}/icons/logo.webp`;     // canonical logo

const metaDescription =
  "Study the Pentacles suit in depth. Explore images and meanings for all fourteen cards that speak to work, money, health, home, skill and steady growth with practical guidance for readings.";

const pageKeywords = `
  tarot, pentacles suit, pentacles tarot meanings, earth element tarot, money in tarot, career in tarot,
  health and body in tarot, home and resources, habit and discipline, grounded progress,
  upright meanings, reversed meanings, learn tarot, tarot study, tarot interpretation,
  ace of pentacles, two of pentacles, three of pentacles, four of pentacles, five of pentacles,
  six of pentacles, seven of pentacles, eight of pentacles, nine of pentacles, ten of pentacles,
  page of pentacles, knight of pentacles, queen of pentacles, king of pentacles,
  beginner tarot, advanced tarot,
  tarot in English, tarot en Español, tarot em Português, tarot en Français,
  tarot Deutsch, tarocchi Italiano, tarot Türkçe, tarot 日本語, tarot 한국어
`.replace(/\s+/g, " ").trim();

/* =========================================================
   Data: exact 14 slugs for Pentacles
========================================================= */
const pentaclesSlugs = [
  "ace-of-pentacles-tarot-card-reading-meaning",
  "two-of-pentacles-tarot-card-reading-meaning",
  "three-of-pentacles-tarot-card-reading-meaning",
  "four-of-pentacles-tarot-card-reading-meaning",
  "five-of-pentacles-tarot-card-reading-meaning",
  "six-of-pentacles-tarot-card-reading-meaning",
  "seven-of-pentacles-tarot-card-reading-meaning",
  "eight-of-pentacles-tarot-card-reading-meaning",
  "nine-of-pentacles-tarot-card-reading-meaning",
  "ten-of-pentacles-tarot-card-reading-meaning",
  "page-of-pentacles-tarot-card-reading-meaning",
  "knight-of-pentacles-tarot-card-reading-meaning",
  "queen-of-pentacles-tarot-card-reading-meaning",
  "king-of-pentacles-tarot-card-reading-meaning",
];

const pentaclesCards = Object.values(cardData).filter((c) =>
  pentaclesSlugs.includes(c.slug)
);

/* =========================================================
   JSON-LD (CollectionPage + ItemList + FAQPage)
========================================================= */
function schemaGraphFor(items, faqEntities) {
  const itemListElement = items.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    url: `${siteUrl}${cardPath(c)}`,
  }));

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}#org`,
        name: "TarotFate",
        url: siteUrl,
        logo: { "@type": "ImageObject", url: logoWebp, width: 512, height: 512 },
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}#website`,
        url: siteUrl,
        name: "TarotFate",
        publisher: { "@id": `${siteUrl}#org` },
        inLanguage: "en",
      },
      {
        "@type": "CollectionPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: "Pentacles Tarot Cards | Meanings and Symbols | TarotFate",
        description: metaDescription,
        isPartOf: { "@id": `${siteUrl}#website` },
        primaryImageOfPage: { "@id": `${pageUrl}#image` },
        breadcrumb: { "@id": `${pageUrl}#breadcrumbs` },
        inLanguage: "en",
      },
      {
        "@type": "ImageObject",
        "@id": `${pageUrl}#image`,
        url: ogImage,
        width: 1200,
        height: 630,
        caption: "TarotFate cover",
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
          { "@type": "ListItem", position: 2, name: "Tarot Cards", item: `${siteUrl}/tarot-cards` },
          { "@type": "ListItem", position: 3, name: "Pentacles", item: pageUrl },
        ],
      },
      {
        "@type": "ItemList",
        "@id": `${pageUrl}#pentacles`,
        name: "Pentacles",
        itemListElement,
      },
      {
        "@type": "FAQPage",
        "@id": `${pageUrl}#faq`,
        mainEntity: faqEntities,
      },
    ],
  };
}

/* =========================================================
   Copy blocks (no dashes in visible text)
========================================================= */
const suitIntro =
  "The Pentacles suit represents work, money, health, home and resource management. Aligned with the element of earth, it charts a path from intention to tangible result through patience and practical wisdom. From the Ace's grounded beginnings to the King's established legacy, these cards emphasize discipline, skill and habits that build lasting value. Pentacles highlight the importance of planning, savings, routines and healthy partnerships for steady progress. Imagery often includes coins, gardens and tools, symbolizing cultivation and structure. When Pentacles appear, it's a time for focused action, ethical choices and clear goals, but beware of overwork or attachment. Prosperity grows with respect for resources, healthy boundaries and steady effort.";

const heroExplainer = "";

export default function Pentacles() {
  const isSpanish = isSpanishLocale();
  // Rich FAQ for on-page copy and JSON-LD
  const faq = isSpanish
    ? [
        {
          q: "Que representan los Oros en el tarot?",
          a: "Oros representa el elemento tierra. Habla del mundo material: trabajo, dinero, cuerpo, hogar, tiempo y cuidado de recursos. En una lectura enfoca crecimiento estable y pasos practicos.",
        },
        {
          q: "Cuantas cartas de Oros hay en una baraja?",
          a: "Hay catorce cartas de Oros, desde el As hasta el Rey. Incluye diez cartas numeradas y cuatro cartas de la corte: Sota, Caballero, Reina y Rey.",
        },
        {
          q: "Oros solo habla de dinero?",
          a: "No. Oros puede hablar de dinero y trabajo, pero tambien de cuerpo, hogar, oficio, servicio y la manera en que usas tu energia.",
        },
        {
          q: "Que significa que aparezcan muchos Oros juntos?",
          a: "Muchos Oros sugieren un periodo centrado en asuntos practicos. La tirada puede pedir planificacion, ritmo sostenible y habitos que apoyen seguridad.",
        },
        {
          q: "Como trabajo con la energia de Oros?",
          a: "Define una meta clara, elige una practica constante y mide el progreso con paciencia. Los resultados confiables nacen de integridad y constancia.",
        },
        {
          q: "Como leo cartas dificiles como Cinco de Oros?",
          a: "Empieza por la necesidad central. Luego identifica apoyo, habilidades y recursos cercanos. La carta suele mostrar como reconstruir confianza y estabilidad.",
        },
        {
          q: "Debo usar significados invertidos con Oros?",
          a: "Usalos si aportan matiz. Si prefieres, lee al derecho y usa la posicion o la pregunta para dar contexto. Elige un metodo y aplicalo con consistencia.",
        },
      ]
    : [
        {
          q: "What do Pentacles represent in tarot?",
          a: "Pentacles represent the element of earth. They describe the material world and include work, money, health, home, time and the care of resources. In a reading they focus attention on steady growth and practical steps.",
        },
        {
          q: "How many Pentacles cards are in a deck?",
          a: "There are fourteen Pentacles cards from Ace through King. The suit includes ten numbered cards and four court cards called Page, Knight, Queen and King.",
        },
        {
          q: "Are Pentacles only about money?",
          a: "No. Pentacles speak about money and career, yet they also include body, home, craft and service. The suit explores how values shape the use of time, energy and resources.",
        },
        {
          q: "What does it mean when many Pentacles appear together?",
          a: "Many Pentacles suggest a period that centers on practical matters. The spread may ask for planning, budgeting, sustainable pace and habits that support health and security.",
        },
        {
          q: "How do I work with the energy of Pentacles?",
          a: "Define a clear goal, choose a consistent practice and measure progress over time. Celebrate small wins and refine methods as you learn. Integrity and patience produce reliable results.",
        },
        {
          q: "How do I read challenging Pentacles like Five of Pentacles?",
          a: "Begin with the core need, then identify support, skills and community. The card often points to resources that are near at hand and to practices that rebuild confidence and stability.",
        },
        {
          q: "Should I use reversed meanings for Pentacles?",
          a: "Use reversals if they add helpful nuance. If you prefer, read upright meanings and rely on position and question for context. Choose one approach and apply it with consistency.",
        },
      ];

  const faqEntities = faq.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  }));

  const schemaGraph = schemaGraphFor(pentaclesCards, faqEntities);
  const title = isSpanish ? "Oros en el Tarot | Significados y Símbolos | TarotFate" : "Pentacles Tarot Cards | Meanings and Symbols | TarotFate";
  const intro = isSpanish
    ? "Oros representa trabajo, cuerpo, dinero, hogar, recursos y crecimiento estable. Este palo ayuda a convertir intención en resultados concretos con paciencia, cuidado y constancia."
    : suitIntro;

  return (
    <div className={`${FONT} text-white min-h-screen relative overflow-hidden`} style={{ background: CANVAS_BG }}>
      {/* SEO (centralized, like About.js) */}
      <Seo
        title={title}
        description={isSpanish ? "Explora Oros en el tarot con imágenes, símbolos y significados claros para trabajo, recursos, cuerpo y crecimiento estable." : metaDescription}
        url={`${siteUrl}${localizedPath("/tarot/pentacles")}`}
        image={ogImage}
        schema={schemaGraph}
        keywords={pageKeywords}
        includeMega={true}
      />

      {/* Ambient auras */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full opacity-25 blur-[160px] bg-[radial-gradient(circle,rgba(59,198,214,0.55)_0%,transparent_70%)]" />
        <div className="absolute -bottom-[25%] right-[5%] w-[700px] h-[700px] rounded-full opacity-20 blur-[140px] bg-[radial-gradient(circle,rgba(140,59,90,0.55)_0%,transparent_70%)]" />
      </div>

      {/* HERO (centered, matches other suit pages) */}
      <section className={`pt-6 ${WRAP} ${MAXW}`}>
        <h1 className="text-xl sm:text-2xl tracking-wide text-center font-bold">{isSpanish ? "Oros en el Tarot" : "Pentacles Tarot Cards"}</h1>
        <div className="flex items-center justify-center mb-1">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
          <span className="px-4 text-[#f3eaff]/100">✦</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
        </div>
        <p className={`${TEXT} text-base mt-2`}>{intro}</p>
        <p className={`${TEXT} text-[15px] md:text-[16px] leading-relaxed mt-2`}>{heroExplainer}</p>
      </section>

      {/* SINGLE SUIT PANEL */}
      <section className={`mt-6 ${WRAP} ${MAXW}`}>
        <div className={`${PANEL_BASE} ${SILK_TURQ} p-3 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <h2 className="text-base sm:text-lg font-semibold uppercase tracking-[0.12em] text-white/95">{translateSuitName("Pentacles").toUpperCase()}</h2>
            <p className={`${TEXT} text-sm leading-relaxed mt-2`}>
              {isSpanish
                ? "Las cartas siguientes muestran el palo completo. Abre cualquier carta para estudiar significado, símbolos y guía para trabajo, dinero, oficio, salud y crecimiento personal."
                : "The cards below present the full suit. Open any card to study upright and reversed meanings, symbolism and guidance for career, money, craft, health and personal development."}
            </p>

            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
              {pentaclesCards.map((card) => (
                <Link
                  key={card.slug}
                  to={localizedPath(cardPath(card))}
                  className="group rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 hover:border-white/25 hover:-translate-y-1 transition-transform duration-100"
                >
                  <div className="p-2 flex flex-col items-center gap-2">
                    {/* Card image on top, fully visible (no crop) */}
                    <div className="w-full aspect-[2/3] rounded-lg bg-white/5 border border-white/10 overflow-hidden">
                      <img
                        src={card.image || `/card/${card.slug}.webp`}
                        alt={translateCardName(card.name)}
                        className="w-full h-full object-contain select-none pointer-events-none"
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = "/icons/logo.webp"; // ✅ consistent webp fallback
                        }}
                      />
                    </div>

                    {/* Title + subtitle below the card */}
                    <div className="text-center">
                      <h3 className="text-sm font-semibold text-white/95">{translateCardName(card.name)}</h3>
                      {card.subtitle ? (
                        <p className="text-xs italic text-white/75">{translateKeywordPhrase(card.subtitle)}</p>
                      ) : null}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
          <div className="absolute inset-x-0 top-0 h-px opacity-60 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        </div>
      </section>

     {/* FAQ (always open, colored Q/A) */}
<section id="faq" className={`mt-12 mb-16 ${WRAP} ${MAXW}`}>
  <div className="text-center">
    <h2 className="text-base sm:text-lg font-medium">{isSpanish ? COMMON_COPY_ES.faq : "Frequently Asked Questions"}</h2>
  </div>

  <div className="mt-6 rounded-sm border border-white/35 bg-white/5">
    <div className="divide-y divide-white/20">
      {faq.map(({ q, a }, i) => (
        <div key={i} className="px-4 md:px-6 py-4">
          {/* Question pill (on-theme) */}
          <p className="font-semibold text-sm sm:text-base leading-relaxed px-3 py-2 rounded-md text-white bg-white/10">
            {q}
          </p>

          {/* Answer */}
          <p className="mt-2 text-white/90 text-sm sm:text-base leading-relaxed px-3">
            {a}
          </p>
        </div>
      ))}
    </div>
  </div>

  {/* Explore other suits */}
  <div className="text-center mt-10">
    <div className="tf-outline-button-row">
      <Link
        to={localizedPath("/tarot/major-arcana")}
        className="tf-outline-button"
      >
        {translateSuitName("Major Arcana")}
      </Link>
      <Link
        to={localizedPath("/tarot/wands")}
        className="tf-outline-button"
      >
        {translateSuitName("Wands")}
      </Link>
      <Link
        to={localizedPath("/tarot/cups")}
        className="tf-outline-button"
      >
        {translateSuitName("Cups")}
      </Link>
      <Link
        to={localizedPath("/tarot/swords")}
        className="tf-outline-button"
      >
        {translateSuitName("Swords")}
      </Link>
      <Link
        to={localizedPath("/tarot-cards")}
        className="tf-outline-button"
      >
        {isSpanish ? COMMON_COPY_ES.allCards : "All Cards"}
      </Link>
    </div>
  </div>
</section>

    </div>
  );
}

